/**
 * Standardized formatting utilities for the entire application
 */

/**
 * Format date in dd/mm/yyyy format (Indian standard)
 * @param date - Date object, string, or null/undefined
 * @returns Formatted date string in dd/mm/yyyy format
 */
export const formatDate = (date: Date | string | null | undefined): string => {
  if (!date) return 'N/A';
  
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  
  if (isNaN(dateObj.getTime())) return 'Invalid Date';
  
  const day = dateObj.getDate().toString().padStart(2, '0');
  const month = (dateObj.getMonth() + 1).toString().padStart(2, '0');
  const year = dateObj.getFullYear();
  
  return `${day}/${month}/${year}`;
};

/**
 * Format amount in Indian numbering system (lakh/crore format) - NO DECIMALS (for sale deed)
 * @param amount - Number to format
 * @returns Formatted amount string with ₹ symbol
 */
export const formatCurrency = (amount: number | null | undefined): string => {
  if (amount === null || amount === undefined || isNaN(amount)) return '₹0';
  
  const absAmount = Math.abs(amount);
  const isNegative = amount < 0;
  
  // Convert to Indian number format (no decimals for sale deed)
  const formatted = Math.round(absAmount).toLocaleString('en-IN');
  
  return `${isNegative ? '-' : ''}₹${formatted}`;
};

/**
 * Format hidden income amounts (terrace, charges, other work) WITH 2 DECIMALS
 * @param amount - Number to format
 * @returns Formatted amount string with ₹ symbol and 2 decimal places
 */
export const formatHiddenIncome = (amount: number | null | undefined): string => {
  if (amount === null || amount === undefined || isNaN(amount)) return '₹0.00';
  
  const absAmount = Math.abs(amount);
  const isNegative = amount < 0;
  
  // Convert to Indian number format with 2 decimals for hidden income
  const formatted = absAmount.toLocaleString('en-IN', { 
    minimumFractionDigits: 2, 
    maximumFractionDigits: 2 
  });
  
  return `${isNegative ? '-' : ''}₹${formatted}`;
};

/**
 * Format amount without currency symbol (for input fields)
 * @param amount - Number to format
 * @returns Formatted amount string without ₹ symbol
 */
export const formatAmount = (amount: number | null | undefined): string => {
  if (amount === null || amount === undefined || isNaN(amount)) return '0';
  
  const absAmount = Math.abs(amount);
  const isNegative = amount < 0;
  
  // Convert to Indian number format
  const formatted = absAmount.toLocaleString('en-IN');
  
  return `${isNegative ? '-' : ''}${formatted}`;
};

/**
 * Format date for input fields (yyyy-mm-dd format)
 * @param date - Date object, string, or null/undefined
 * @returns Formatted date string in yyyy-mm-dd format for input fields
 */
export const formatDateForInput = (date: Date | string | null | undefined): string => {
  if (!date) return '';
  
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  
  if (isNaN(dateObj.getTime())) return '';
  
  const year = dateObj.getFullYear();
  const month = (dateObj.getMonth() + 1).toString().padStart(2, '0');
  const day = dateObj.getDate().toString().padStart(2, '0');
  
  return `${year}-${month}-${day}`;
};

/**
 * Parse date from input field (yyyy-mm-dd) to Date object
 * @param dateString - Date string in yyyy-mm-dd format
 * @returns Date object or null if invalid
 */
export const parseDateFromInput = (dateString: string): Date | null => {
  if (!dateString) return null;
  
  const date = new Date(dateString);
  return isNaN(date.getTime()) ? null : date;
};

/**
 * Get maximum date allowed for transaction date inputs (today)
 * Returns date in yyyy-mm-dd format for use with max attribute
 * @returns Today's date in yyyy-mm-dd format
 */
export const getMaxTransactionDate = (): string => {
  const today = new Date();
  const year = today.getFullYear();
  const month = (today.getMonth() + 1).toString().padStart(2, '0');
  const day = today.getDate().toString().padStart(2, '0');
  return `${year}-${month}-${day}`;
};
